import capitalize from 'lodash.capitalize';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type {
  Breadcrumb,
  Creative,
  Flavors,
  ICustomData,
  IUTMParams,
  Partner,
} from '@feathr/blackbox';
import { FieldCollection } from '@feathr/blackbox';
import { Button, Chip, Fieldset, Form, Time, Value } from '@feathr/components';
import DeviceChip from '@feathr/extender/components/DeviceChip';
import MarkerMap from '@feathr/extender/components/MarkerMap';
import { StoresContext, useLocalUrl } from '@feathr/extender/state';
import { TimeFormat, useToggle } from '@feathr/hooks';

import BrowserChip from '../BrowserChip';
import type { IProps as IBrowserChipProps } from '../BrowserChip/BrowserChip';
import FlavorChip from '../FlavorChip';
import type { TFlavor } from '../FlavorChip/FlavorChip';
import PlatformChip from '../PlatformChip';
import type { IProps as IPlatformChipProps } from '../PlatformChip/PlatformChip';
import BreadcrumbRawModal from './BreadcrumbRawModal';
import CampaignTLDR from './CampaignTLDR';
import ContactListTLDR from './ContactListTLDR';
import CreativeTLDR from './CreativeTLDR';
import EventTLDR from './EventTLDR';
import PartnerTLDR from './PartnerTLDR';

import * as styles from './BreadcrumbDetails.css';

export interface IBreadcrumbDetailsProps {
  breadcrumb: Breadcrumb;
  hasViewRawData?: boolean;
  context: FieldCollection.Person | FieldCollection.Partner;
}

interface IProjectData {
  action: '+' | '-';
  id: string;
  name: string;
}

const FlavorDescriptionMap: Record<keyof Flavors, string> = {
  ad_click: 'clicked on an ad.',
  ad_view: 'saw an ad.',
  conversion: 'was marked as a conversion.',
  custom: 'performed a custom activity.',
  dashboard_create: 'created their dashboard.',
  dashboard_import: 'uploaded a contact list.',
  dashboard_send_complete: 'sent email invites.',
  dashboard_send_request: 'scheduled an email invite.',
  dashboard_submit: 'updated their profile.',
  dashboard_view: 'viewed their dashboard.',
  email_preferences_update: 'updated their email preferences.',
  partner_message_link_click: 'clicked a link in a partner message email.',
  partner_message_send: 'received a partner message email.',
  partner_message_view: 'opened a partner message email.',
  created_by_import: 'was created by an import:',
  updated_by_import: 'was updated by import:',
  page_link_click: 'clicked a link.',
  page_view: 'viewed a page.',
  report_view: 'viewed a campaign report.',
  update: 'updated their profile.',
  form_submission: 'submitted a Feathr form.',
  convert: 'was marked as an advanced conversion.',
  pinpoint_tracked_email_hardbounce: 'hard bounced an email.',
  pinpoint_tracked_email_softbounce: 'soft bounced an email.',
  pinpoint_tracked_email_suppression: 'had an email cancelled to protect your sending reputation.',
  pinpoint_tracked_email_open: 'opened an email.',
  pinpoint_tracked_email_click: 'clicked a link in an email.',
  pinpoint_tracked_email_complaint: 'reported an email as spam.',
  pinpoint_tracked_email_send: 'was sent an email.',
  pinpoint_tracked_email_delivered: 'received an email.',
  pinpoint_tracked_email_rendering_failure: 'had an email template rendering failure.',
  raisers_edge_constituent_created: "was created by Raiser's Edge NXT.",
  raisers_edge_constituent_updated: "was updated by Raiser's Edge NXT.",
  raisers_edge_constituent_summary: "had their gift summary updated by Raiser's Edge NXT.",
  raisers_edge_gift_added: "had a gift added by Raiser's Edge NXT.",
  zapier_update: 'was updated by imported data from Zapier.',
  zapier_import: 'was created by imported data from Zapier.',
  user_update: 'was updated by a member of your organization.',
  imis_create: 'was created by iMIS.',
  imis_update: 'was updated by iMIS.',
};

type TFlavorKeys = keyof Flavors;

const payloadFlvrExclusions: TFlavorKeys[] = ['email_preferences_update'];

function BreadcrumbDetails({
  context,
  breadcrumb,
  hasViewRawData = true,
}: IBreadcrumbDetailsProps): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const {
    Campaigns,
    Creatives,
    ContactLists,
    Events,
    Partners,
    Persons,
    SendRequests,
    Tags,
    Importers,
  } = useContext(StoresContext);
  const [toggleShowRawData, setToggleShowRawData] = useToggle(false);

  const id = breadcrumb.get(context === FieldCollection.Partner ? 'p_id' : 'per_id');
  const individual = context === FieldCollection.Partner ? Partners.get(id) : Persons.get(id);

  const eventId = breadcrumb.get('e_id');
  const event = eventId ? Events.get(eventId) : undefined;

  const contactListId = breadcrumb.get('cl_id');
  const contactList = contactListId ? ContactLists.get(contactListId) : undefined;

  const sendRequestId = breadcrumb.get('sreq_id');
  const campaignId = breadcrumb.get('cpn_id');
  const sendRequest = sendRequestId && campaignId ? SendRequests.get(sendRequestId) : undefined;
  const campaign = campaignId ? Campaigns.get(campaignId) : undefined;

  const creativeId = breadcrumb.get('crv_id');
  const creative = creativeId ? Creatives.get(creativeId) : undefined;

  const tagId = breadcrumb.get('tag_id');
  const tag = tagId ? Tags.get(tagId) : undefined;

  const importId = breadcrumb.get('import_id');
  const currentImport = importId ? Importers.get(importId) : undefined;
  const importName = currentImport ? currentImport?.get('name') : undefined;

  const action =
    FlavorDescriptionMap[breadcrumb.get('flvr')] ||
    `dropped a ${breadcrumb.get('flvr')} breadcrumb`;

  const name =
    id !== undefined && !!individual && !individual.isPending ? individual.name : 'Unknown person';

  const custom: ICustomData = breadcrumb.get('custom_data', {});
  const location = breadcrumb.get('loc');
  const payload: ICustomData = breadcrumb.get('payload', {});
  const msg = breadcrumb.get('msg');
  const othersList = breadcrumb.get('others_list');
  const othersListDisplay = othersList ? JSON.parse(othersList[0]) : [];
  const referrer = breadcrumb.get('rfr');
  const source = breadcrumb.get('loc_url');
  const target = breadcrumb.get('trgt');
  const utmParams = breadcrumb.get('utm_params', {} as IUTMParams);
  const breadcrumbFlvr = breadcrumb.get('flvr') as TFlavor;
  const msgDisplay =
    msg && breadcrumbFlvr === 'email_preferences_update' ? JSON.parse(msg) : undefined;

  let dataLabel = '';

  switch (breadcrumbFlvr) {
    case 'imis_create':

    case 'zapier_import':
      dataLabel = t('Data Imported');
      break;

    case 'imis_update':

    case 'zapier_update':
      dataLabel = t('Data Updated');
      break;

    case 'created_by_import':

    case 'updated_by_import':
      dataLabel = t('Details');
      break;

    default:
      dataLabel = t('Payload');
  }

  function formatPayloadLabel(key: string): string {
    return capitalize(key).replaceAll('_', ' ').replaceAll('-', ' ');
  }

  function formatPayloadValue(payload: ICustomData, key: string): string {
    return payload[key]!.toString();
  }

  const optIns =
    msgDisplay?.projects
      .filter((project: IProjectData) => {
        return project.action === '+';
      })
      .map((project: IProjectData) => {
        return project.name;
      }) ?? [];

  const optOuts =
    msgDisplay?.projects
      .filter((project: IProjectData) => {
        return project.action === '-';
      })
      .map((project: IProjectData) => {
        return project.name;
      }) ?? [];

  return (
    // Activity, name, action and date code block
    <Form label={t('Activity details')}>
      <Fieldset>
        <div className={styles.flavor}>
          <FlavorChip flavor={breadcrumbFlvr} />
          <Time format={TimeFormat.shortDateTime} timestamp={breadcrumb.get('d_c')} />
          <span aria-label={'Flavor description'} className={styles.flavorDescription}>
            {name} {action}
          </span>
        </div>
        <div>
          {breadcrumbFlvr === 'created_by_import' || 'updated_by_import' ? (
            <Link className={styles.link} to={localUrl(`/data/imports/${importId}`)} type={'link'}>
              {importName}
            </Link>
          ) : (
            <></>
          )}
        </div>
      </Fieldset>
      {/* platform, browser and screen width code block */}
      {!!(breadcrumb.get('pform') || breadcrumb.get('brow') || breadcrumb.get('s_w')) && (
        <Fieldset direction={'column'} label={'Context'}>
          <Value label={t('Device')} value={<DeviceChip breadcrumb={breadcrumb} />} />
          <Value
            label={t('Platform')}
            value={
              <PlatformChip platform={breadcrumb.get('pform') as IPlatformChipProps['platform']} />
            }
          />
          <Value
            label={t('Browser')}
            value={<BrowserChip browser={breadcrumb.get('brow') as IBrowserChipProps['browser']} />}
          />
        </Fieldset>
      )}
      {/* source, referrer and target */}
      {!!(source || referrer || target) && (
        <Fieldset label={t('Connections')}>
          {!!source && <Value label={t('Source URL')} type={'pre'} value={source} />}
          {!!referrer && <Value label={t('Referrer URL')} type={'pre'} value={referrer} />}
          {!!target && <Value label={t('Target URL')} type={'pre'} value={target} />}
        </Fieldset>
      )}

      {/* display email opt outs/opt ins  */}
      {msgDisplay && breadcrumbFlvr === 'email_preferences_update' && (
        <>
          {(msgDisplay.global || !!optOuts.length) && (
            <Fieldset label={t('Opted out of emails from')}>
              {msgDisplay.global && <Value label={'All Emails'} type={'pre'} value={'true'} />}
              {optOuts.length ? (
                <Value
                  label={t('Project Name', { count: optOuts.length })}
                  type={'pre'}
                  value={optOuts.join(', ')}
                />
              ) : null}
            </Fieldset>
          )}
          {optIns.length ? (
            <Fieldset label={t('Opted in to emails from')}>
              <Value
                label={t('Project Name', { count: optIns.length })}
                type={'pre'}
                value={optIns.join(', ')}
              />
            </Fieldset>
          ) : null}
        </>
      )}

      {/*
        Payload display, which is ICustomData object
        Do not display payload if breadcrumbFlvr is in exclusions
      */}
      {!payloadFlvrExclusions.includes(breadcrumbFlvr) && Object.keys(payload).length > 0 && (
        // Label is payload unless zapier import or update
        <Fieldset label={dataLabel}>
          {Object.keys(payload)
            .filter((key) => payload[key])
            .map((key) => (
              <Value
                key={key}
                label={formatPayloadLabel(key)}
                type={'pre'}
                value={formatPayloadValue(payload, key)}
              />
            ))}
        </Fieldset>
      )}
      {/* UTM data display */}
      {Object.keys(utmParams).length > 0 && (
        <Fieldset label={t('UTM data')}>
          {Object.keys(utmParams)
            .filter((key) => utmParams[key])
            .map((key) => (
              <Value
                key={key}
                label={capitalize(key)}
                type={'pre'}
                value={utmParams[key].toString()}
              />
            ))}
        </Fieldset>
      )}
      {/* Custom Data display */}
      {Object.keys(custom).length > 0 && (
        <Fieldset label={t('Custom data')}>
          {Object.keys(custom)
            .filter((key) => custom[key])
            .map((key) => (
              <Value
                key={key}
                label={capitalize(key)}
                type={'pre'}
                value={custom[key]!.toString()}
              />
            ))}
        </Fieldset>
      )}
      {/* Others list display */}
      {Object.keys(othersListDisplay).length > 0 && (
        <Fieldset label={t('Details')}>
          {Object.keys(othersListDisplay)
            .filter((key) => othersListDisplay![key])
            .map((key) => (
              <Value
                key={key}
                label={capitalize(key).replace('_', ' ')}
                type={'pre'}
                value={othersListDisplay[key]!.toString()}
              />
            ))}
        </Fieldset>
      )}

      {/* tag display */}
      {tag && !tag.isPending && (
        <Value label={t('Tag')} value={<Chip theme={'tag'}>{tag.name}</Chip>} />
      )}
      {/* Map Display */}
      {!!location && (
        <MarkerMap className={styles.map} lat={location.latitude!} lng={location.longitude!} />
      )}
      {/* Creatives Display */}
      {creative && !creative.isPending && (
        <Fieldset direction={'column'} label={'Creative'}>
          <CreativeTLDR creative={creative as Creative} />
        </Fieldset>
      )}
      {/* associated Project display */}
      {!!eventId && (
        <Fieldset label={t('Project')}>
          <EventTLDR event={event} />
        </Fieldset>
      )}
      {/* associated campaign display */}
      {campaign && !campaign.isPending && (
        <Fieldset label={t('Campaign')}>
          <CampaignTLDR campaign={campaign} />
        </Fieldset>
      )}
      {!!id && context === FieldCollection.Partner && (
        <Fieldset label={t('Partner')}>
          <PartnerTLDR partner={individual as Partner} />
        </Fieldset>
      )}
      {!!contactListId && (
        <Fieldset label={t('Contact list')}>
          <ContactListTLDR contactList={contactList} />
        </Fieldset>
      )}
      {sendRequest && !sendRequest.isPending && campaign && !campaign.isPending && (
        <Fieldset label={t('Email invite send')}>
          <Value label={t('Campaign')} value={campaign.name} />
          <Value
            label={t('Date')}
            value={
              <Time format={TimeFormat.shortDateTime} timestamp={sendRequest.get('date_to_send')} />
            }
          />
          <Value label={t('Status')} value={capitalize(sendRequest.get('state'))} />
        </Fieldset>
      )}

      {hasViewRawData && <Button onClick={setToggleShowRawData}>Show raw data</Button>}
      {toggleShowRawData && (
        <BreadcrumbRawModal breadcrumb={breadcrumb} toggle={setToggleShowRawData} />
      )}
    </Form>
  );
}

export default observer(BreadcrumbDetails);
